import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.educential.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---- Auth token helpers ----

export function setAuthToken(token) {
  if (!token) return;
  api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  try {
    localStorage.setItem("educential_token", token);
  } catch (e) {
    console.warn("Unable to persist token:", e);
  }
}

export function clearAuthToken() {
  delete api.defaults.headers.common["Authorization"];
  try {
    localStorage.removeItem("educential_token");
  } catch (e) {
    console.warn("Unable to clear token:", e);
  }
}

(function hydrateTokenFromStorage() {
  try {
    const saved = localStorage.getItem("educential_token");
    if (saved) {
      api.defaults.headers.common["Authorization"] = `Bearer ${saved}`;
    }
  } catch (e) {
    // ignore
  }
})();

// ---- Auth endpoints ----

export async function signup(payload) {
  // payload: { email, password }
  const res = await api.post("/api/auth/signup", payload);
  return res.data;
}

export async function login(payload) {
  // payload: { email, password }
  const res = await api.post("/api/auth/login", payload);
  const data = res.data;

  if (data && data.accessToken) {
    setAuthToken(data.accessToken);
  }

  return data;
}

export async function verifyEmail(token) {
  const res = await api.post("/api/auth/verify-email", { token });
  return res.data;
}

export async function resendVerification(email) {
  const res = await api.post("/api/auth/resend-verification", { email });
  return res.data;
}

export async function forgotPassword(email) {
  const res = await api.post("/api/auth/forgot-password", { email });
  return res.data;
}

export async function resetPassword(token, password) {
  const res = await api.post("/api/auth/reset-password", { token, password });
  return res.data;
}

// ---- Dashboard ----

// New role-based dashboard
export async function getDashboard() {
  // protected route – requires token - returns role-specific dashboard
  const res = await api.get("/api/dashboard");
  return res.data;
}

// Legacy overseas dashboard (for backward compatibility)
export async function getOverseasDashboard() {
  const res = await api.get("/overseas/dashboard");
  return res.data;
}

// ---- Services ----

export async function getAllServices() {
  const res = await api.get("/api/services");
  return res.data;
}

export async function enrollInService(serviceId) {
  const res = await api.post(`/api/services/${serviceId}/enroll`);
  return res.data;
}

export async function getServiceStatus(serviceId) {
  const res = await api.get(`/api/services/${serviceId}/status`);
  return res.data;
}

export async function updateServiceProgress(serviceId, progress) {
  const res = await api.patch(`/api/services/${serviceId}/progress`, { progress });
  return res.data;
}

export async function unenrollFromService(serviceId) {
  const res = await api.delete(`/api/services/${serviceId}/unenroll`);
  return res.data;
}

// ---- Overseas feature: Pathfinder ----

export async function runPathfinder(country, payload) {
  // country: 'UK', 'USA', etc
  const res = await api.post(`/api/overseas/${country}/pathfinder`, payload);
  return res.data;
}

// ---- Overseas: Financial Analysis ----

export async function runFinancialAnalysis(country, payload) {
  const res = await api.post(
    `/api/overseas/${country}/financial-analysis`,
    payload
  );
  return res.data;
}

// ---- Overseas: Education Readiness ----

export async function runReadinessAssessment(country, payload) {
  const res = await api.post(
    `/api/overseas/${country}/readiness-assessment`,
    payload
  );
  return res.data;
}

// ---- Overseas: PoF Calculator ----

export async function runPofCalculator(country, payload) {
  // payload: { totalTuitionFee, tuitionDepositPaid, numberOfDependents, schoolLocation }
  const res = await api.post(
    `/api/overseas/${country}/pof-calculator`,
    payload
  );
  return res.data;
}

// ---- Overseas: Application (Document submission etc) ----

export async function getUserApplication(country) {
  const res = await api.get(`/api/overseas/${country}/application`);
  return res.data;
}

export async function submitApplication(country, payload) {
  const res = await api.post(`/api/overseas/${country}/apply`, payload);
  return res.data;
}

// ---- User Profile ----

export async function getProfile() {
  const res = await api.get("/api/user/profile");
  return res.data;
}

export async function updateProfile(payload) {
  // payload: { fullName, phone, country, city, occupation, bio }
  const res = await api.put("/api/user/profile", payload);
  return res.data;
}

// ---- User Settings ----

export async function changePassword(payload) {
  // payload: { currentPassword, newPassword }
  const res = await api.post("/api/user/change-password", payload);
  return res.data;
}

export async function updateNotificationPreferences(payload) {
  const res = await api.put("/api/user/notifications", payload);
  return res.data;
}

export async function getNotificationPreferences() {
  const res = await api.get("/api/user/notifications");
  return res.data;
}

export async function updatePrivacySettings(payload) {
  const res = await api.put("/api/user/privacy", payload);
  return res.data;
}

export async function getPrivacySettings() {
  const res = await api.get("/api/user/privacy");
  return res.data;
}

export async function deleteAccount() {
  const res = await api.delete("/api/user/account");
  return res.data;
}

export default api;
// ---- Notifications ----

export async function getNotifications(limit = 20) {
  const res = await api.get(`/api/notifications?limit=${limit}`);
  return res.data;
}

export async function getUnreadCount() {
  const res = await api.get("/api/notifications/unread-count");
  return res.data;
}

export async function markNotificationAsRead(id) {
  const res = await api.put(`/api/notifications/${id}/read`);
  return res.data;
}

export async function markAllNotificationsAsRead() {
  const res = await api.put("/api/notifications/read-all");
  return res.data;
}

export async function deleteNotification(id) {
  const res = await api.delete(`/api/notifications/${id}`);
  return res.data;
}

// ---- Educator: Student Management ----

export async function getEducatorStudents() {
  const res = await api.get("/api/educator/students");
  return res.data;
}

export async function getStudentDetail(studentId) {
  const res = await api.get(`/api/educator/students/${studentId}`);
  return res.data;
}

export async function addStudentNote(studentId, note) {
  const res = await api.post(`/api/educator/students/${studentId}/notes`, { note });
  return res.data;
}

export async function assignStudent(studentId) {
  const res = await api.post("/api/educator/students/assign", { studentId });
  return res.data;
}

export async function unassignStudent(studentId) {
  const res = await api.delete(`/api/educator/students/${studentId}`);
  return res.data;
}

// ---- Educator: Content Management ----

export async function getEducatorContent(filters = {}) {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.published !== undefined) params.append('published', filters.published);
  if (filters.search) params.append('search', filters.search);

  const res = await api.get(`/api/educator/content?${params.toString()}`);
  return res.data;
}

export async function getContentById(contentId) {
  const res = await api.get(`/api/educator/content/${contentId}`);
  return res.data;
}

export async function createContent(data) {
  const res = await api.post("/api/educator/content", data);
  return res.data;
}

export async function updateContent(contentId, data) {
  const res = await api.patch(`/api/educator/content/${contentId}`, data);
  return res.data;
}

export async function deleteContent(contentId) {
  const res = await api.delete(`/api/educator/content/${contentId}`);
  return res.data;
}

export async function toggleContentPublish(contentId, published) {
  const res = await api.patch(`/api/educator/content/${contentId}/publish`, { published });
  return res.data;
}

// ---- Career Counseling Service ----

export async function submitCareerAssessment(assessmentData) {
  const res = await api.post("/api/career/assessment", assessmentData);
  return res.data;
}

export async function bookCareerSession(sessionData) {
  const res = await api.post("/api/career/sessions/book", sessionData);
  return res.data;
}

export async function getCareerSessions() {
  const res = await api.get("/api/career/sessions");
  return res.data;
}

export async function createCareerGoal(goalData) {
  const res = await api.post("/api/career/goals", goalData);
  return res.data;
}

export async function getCareerGoals() {
  const res = await api.get("/api/career/goals");
  return res.data;
}

export async function updateCareerGoalProgress(goalId, progress) {
  const res = await api.patch(`/api/career/goals/${goalId}/progress`, { progress });
  return res.data;
}

export async function createCareerPlan(planData) {
  const res = await api.post("/api/career/plan", planData);
  return res.data;
}

export async function getCareerPlan() {
  const res = await api.get("/api/career/plan");
  return res.data;
}

// ---- Skill Development Service ----

export async function getAllCourses(filters = {}) {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.level) params.append('level', filters.level);
  if (filters.search) params.append('search', filters.search);

  const res = await api.get(`/api/skills/courses?${params.toString()}`);
  return res.data;
}

export async function getSkillsCourseById(courseId) {
  const res = await api.get(`/api/skills/courses/${courseId}`);
  return res.data;
}

export async function enrollInSkillsCourse(courseId) {
  const res = await api.post("/api/skills/enroll", { courseId });
  return res.data;
}

export async function getMyLearning() {
  const res = await api.get("/api/skills/my-learning");
  return res.data;
}

export async function updateLessonProgress(courseId, lessonId, completed) {
  const res = await api.patch("/api/skills/progress", { courseId, lessonId, completed });
  return res.data;
}

export async function getLearningPaths() {
  const res = await api.get("/api/skills/learning-paths");
  return res.data;
}

export async function getMyCertifications() {
  const res = await api.get("/api/skills/certifications");
  return res.data;
}

export async function getSkillProgress() {
  const res = await api.get("/api/skills/progress");
  return res.data;
}

// ---- Test Preparation Service ----

export async function getAvailableTests() {
  const res = await api.get("/api/test-prep/tests");
  return res.data;
}

export async function enrollInTest(testId) {
  const res = await api.post("/api/test-prep/enroll", { testId });
  return res.data;
}

export async function getUserTestEnrollments() {
  const res = await api.get("/api/test-prep/my-tests");
  return res.data;
}

export async function submitPracticeTest(testId, score) {
  const res = await api.post("/api/test-prep/practice-test", { testId, score });
  return res.data;
}

// ---- Scholarship Finder Service ----

export async function searchScholarships(filters = {}) {
  const params = new URLSearchParams();
  if (filters.country) params.append('country', filters.country);
  if (filters.level) params.append('level', filters.level);

  const res = await api.get(`/api/scholarships/search?${params.toString()}`);
  return res.data;
}

export async function getScholarshipById(scholarshipId) {
  const res = await api.get(`/api/scholarships/${scholarshipId}`);
  return res.data;
}

export async function saveScholarship(scholarshipId) {
  const res = await api.post(`/api/scholarships/${scholarshipId}/save`);
  return res.data;
}

export async function getSavedScholarships() {
  const res = await api.get("/api/scholarships/user/saved");
  return res.data;
}

export async function applyForScholarship(scholarshipId, applicationData) {
  const res = await api.post(`/api/scholarships/${scholarshipId}/apply`, applicationData);
  return res.data;
}

// ---- Accommodation Service ----

export async function searchAccommodation(filters = {}) {
  const params = new URLSearchParams();
  if (filters.location) params.append('location', filters.location);
  if (filters.type) params.append('type', filters.type);
  if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);

  const res = await api.get(`/api/accommodation/search?${params.toString()}`);
  return res.data;
}

export async function saveAccommodation(accommodationId) {
  const res = await api.post(`/api/accommodation/${accommodationId}/save`);
  return res.data;
}

export async function contactLandlord(accommodationId, message) {
  const res = await api.post(`/api/accommodation/${accommodationId}/contact`, { message });
  return res.data;
}

// ---- Pre-Departure Service ----

export async function getPreDepartureChecklist(country) {
  const res = await api.get(`/api/pre-departure/checklist?country=${country}`);
  return res.data;
}

export async function markChecklistItemComplete(itemId) {
  const res = await api.post(`/api/pre-departure/checklist/${itemId}/complete`);
  return res.data;
}

export async function getOrientationSessions(country) {
  const res = await api.get(`/api/pre-departure/orientation?country=${country}`);
  return res.data;
}

// ---- Post-Arrival Service ----

export async function getPostArrivalTasks() {
  const res = await api.get("/api/post-arrival/tasks");
  return res.data;
}

export async function completePostArrivalTask(taskId) {
  const res = await api.post(`/api/post-arrival/tasks/${taskId}/complete`);
  return res.data;
}

export async function getSupportResources() {
  const res = await api.get("/api/post-arrival/support");
  return res.data;
}

// ---- Alumni Network Service ----

export async function searchAlumni(filters = {}) {
  const params = new URLSearchParams();
  if (filters.university) params.append('university', filters.university);
  if (filters.country) params.append('country', filters.country);

  const res = await api.get(`/api/alumni/directory?${params.toString()}`);
  return res.data;
}

export async function sendConnectionRequest(alumniId, message) {
  const res = await api.post(`/api/alumni/connect/${alumniId}`, { message });
  return res.data;
}

export async function getAlumniEvents() {
  const res = await api.get("/api/alumni/events");
  return res.data;
}

export async function getUserConnections() {
  const res = await api.get("/api/alumni/connections");
  return res.data;
}

// ---- Scratch Cards E-Commerce ----

// Public endpoints
export async function getAllScratchCardProducts() {
  const res = await api.get("/api/scratch-cards/products");
  return res.data;
}

export async function getScratchCardProduct(id) {
  const res = await api.get(`/api/scratch-cards/products/${id}`);
  return res.data;
}

// User endpoints
export async function purchaseScratchCard(data) {
  const res = await api.post("/api/scratch-cards/purchase", data);
  return res.data;
}

export async function getMyPurchases() {
  const res = await api.get("/api/scratch-cards/my-purchases");
  return res.data;
}

export async function verifyPaystackPayment(data) {
  const res = await api.post("/api/scratch-cards/verify-payment", data);
  return res.data;
}

// Admin endpoints
export async function createScratchCardProduct(data) {
  const res = await api.post("/api/scratch-cards/admin/products", data);
  return res.data;
}

export async function updateScratchCardProduct(id, data) {
  const res = await api.put(`/api/scratch-cards/admin/products/${id}`, data);
  return res.data;
}

export async function deleteScratchCardProduct(id) {
  const res = await api.delete(`/api/scratch-cards/admin/products/${id}`);
  return res.data;
}

export async function toggleScratchCardProduct(id) {
  const res = await api.patch(`/api/scratch-cards/admin/products/${id}/toggle`);
  return res.data;
}

export async function getScratchCardStats() {
  const res = await api.get("/api/scratch-cards/admin/stats");
  return res.data;
}

export async function getAllScratchCardPurchases(filters = {}) {
  const params = new URLSearchParams(filters);
  const res = await api.get(`/api/scratch-cards/admin/purchases?${params.toString()}`);
  return res.data;
}

// ==================== LMS Courses API ====================

// ---- Educator Endpoints ----

export async function createCourse(courseData) {
  const res = await api.post("/api/courses", courseData);
  return res.data;
}

export async function getEducatorCourses(filters = {}) {
  const params = new URLSearchParams(filters);
  const res = await api.get(`/api/courses/my-courses?${params.toString()}`);
  return res.data;
}

export async function getCourseById(courseId) {
  const res = await api.get(`/api/courses/${courseId}`);
  return res.data;
}

export async function updateCourse(courseId, courseData) {
  const res = await api.patch(`/api/courses/${courseId}`, courseData);
  return res.data;
}

export async function deleteCourse(courseId) {
  const res = await api.delete(`/api/courses/${courseId}`);
  return res.data;
}

export async function toggleCoursePublish(courseId) {
  const res = await api.patch(`/api/courses/${courseId}/publish`);
  return res.data;
}

// Module Management
export async function createModule(courseId, moduleData) {
  const res = await api.post(`/api/courses/${courseId}/modules`, moduleData);
  return res.data;
}

export async function updateModule(courseId, moduleId, moduleData) {
  const res = await api.patch(`/api/courses/${courseId}/modules/${moduleId}`, moduleData);
  return res.data;
}

export async function deleteModule(courseId, moduleId) {
  const res = await api.delete(`/api/courses/${courseId}/modules/${moduleId}`);
  return res.data;
}

export async function reorderModules(courseId, items) {
  const res = await api.patch(`/api/courses/${courseId}/modules/reorder`, { items });
  return res.data;
}

// Lesson Management
export async function createLesson(moduleId, lessonData) {
  const res = await api.post(`/api/courses/modules/${moduleId}/lessons`, lessonData);
  return res.data;
}

export async function updateLesson(moduleId, lessonId, lessonData) {
  const res = await api.patch(`/api/courses/modules/${moduleId}/lessons/${lessonId}`, lessonData);
  return res.data;
}

export async function deleteLesson(moduleId, lessonId) {
  const res = await api.delete(`/api/courses/modules/${moduleId}/lessons/${lessonId}`);
  return res.data;
}

export async function reorderLessons(moduleId, items) {
  const res = await api.patch(`/api/courses/modules/${moduleId}/lessons/reorder`, { items });
  return res.data;
}

export async function getLesson(lessonId) {
  const res = await api.get(`/api/courses/lessons/${lessonId}`);
  return res.data;
}

// ---- Student Endpoints ----

export async function getCourseCatalog(filters = {}) {
  const params = new URLSearchParams(filters);
  const res = await api.get(`/api/courses/catalog?${params.toString()}`);
  return res.data;
}

export async function enrollInCourse(courseId) {
  const res = await api.post(`/api/courses/${courseId}/enroll`);
  return res.data;
}

export async function getEnrolledCourses() {
  const res = await api.get("/api/courses/my-learning");
  return res.data;
}

export async function getCourseContent(courseId) {
  const res = await api.get(`/api/courses/${courseId}/content`);
  return res.data;
}

// ---- Progress Tracking ----

export async function markLessonComplete(lessonId) {
  const res = await api.post(`/api/courses/lessons/${lessonId}/complete`);
  return res.data;
}

export async function getCourseProgress(courseId) {
  const res = await api.get(`/api/courses/${courseId}/progress`);
  return res.data;
}

// ---- Analytics ----

export async function getCourseAnalytics(courseId) {
  const res = await api.get(`/api/courses/${courseId}/analytics`);
  return res.data;
}

export async function getCourseStudents(courseId) {
  const res = await api.get(`/api/courses/${courseId}/students`);
  return res.data;
}

// ============ QUIZ SYSTEM ============

// ---- Educator: Quiz Management ----

export async function createQuiz(lessonId, data) {
  const res = await api.post(`/api/quiz/lessons/${lessonId}/quiz`, data);
  return res.data;
}

export async function getQuiz(quizId, includeAnswers = false) {
  const res = await api.get(`/api/quiz/quizzes/${quizId}?includeAnswers=${includeAnswers}`);
  return res.data;
}

export async function updateQuiz(quizId, data) {
  const res = await api.patch(`/api/quiz/quizzes/${quizId}`, data);
  return res.data;
}

export async function deleteQuiz(quizId) {
  const res = await api.delete(`/api/quiz/quizzes/${quizId}`);
  return res.data;
}

// ---- Question Management ----

export async function createQuestion(quizId, data) {
  const res = await api.post(`/api/quiz/quizzes/${quizId}/questions`, data);
  return res.data;
}

export async function updateQuestion(questionId, data) {
  const res = await api.patch(`/api/quiz/questions/${questionId}`, data);
  return res.data;
}

export async function deleteQuestion(questionId) {
  const res = await api.delete(`/api/quiz/questions/${questionId}`);
  return res.data;
}

export async function reorderQuestions(quizId, items) {
  const res = await api.patch(`/api/quiz/quizzes/${quizId}/questions/reorder`, { items });
  return res.data;
}

// ---- Student: Quiz Taking ----

export async function startQuiz(quizId) {
  const res = await api.post(`/api/quiz/quizzes/${quizId}/start`);
  return res.data;
}

export async function submitQuiz(attemptId, answers) {
  const res = await api.post(`/api/quiz/quiz-attempts/${attemptId}/submit`, { answers });
  return res.data;
}

export async function getQuizAttempt(attemptId) {
  const res = await api.get(`/api/quiz/quiz-attempts/${attemptId}`);
  return res.data;
}

export async function getStudentQuizAttempts(quizId) {
  const res = await api.get(`/api/quiz/quizzes/${quizId}/attempts`);
  return res.data;
}

// ---- Quiz Analytics ----

export async function getQuizAnalytics(quizId) {
  const res = await api.get(`/api/quiz/quizzes/${quizId}/analytics`);
  return res.data;
}

// ---- Assignment System ----

// Educator: Assignment Management

export async function createAssignment(lessonId, data) {
  const res = await api.post(`/api/assignment/lessons/${lessonId}/assignment`, data);
  return res.data;
}

export async function getAssignment(assignmentId) {
  const res = await api.get(`/api/assignment/assignments/${assignmentId}`);
  return res.data;
}

export async function updateAssignment(assignmentId, data) {
  const res = await api.patch(`/api/assignment/assignments/${assignmentId}`, data);
  return res.data;
}

export async function deleteAssignment(assignmentId) {
  const res = await api.delete(`/api/assignment/assignments/${assignmentId}`);
  return res.data;
}

export async function getAssignmentSubmissions(assignmentId) {
  const res = await api.get(`/api/assignment/assignments/${assignmentId}/submissions`);
  return res.data;
}

// Student: Assignment Submission

export async function submitAssignment(assignmentId, data, file) {
  const formData = new FormData();
  if (data.textResponse) {
    formData.append('textResponse', data.textResponse);
  }
  if (file) {
    formData.append('file', file);
  }

  const res = await api.post(`/api/assignment/assignments/${assignmentId}/submit`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
}

export async function updateSubmission(submissionId, data, file) {
  const formData = new FormData();
  if (data.textResponse) {
    formData.append('textResponse', data.textResponse);
  }
  if (file) {
    formData.append('file', file);
  }

  const res = await api.patch(`/api/assignment/submissions/${submissionId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
}

export async function getStudentSubmission(assignmentId) {
  const res = await api.get(`/api/assignment/assignments/${assignmentId}/submission`);
  return res.data;
}

// Educator: Grading

export async function gradeSubmission(submissionId, data) {
  const res = await api.post(`/api/assignment/submissions/${submissionId}/grade`, data);
  return res.data;
}

export async function returnSubmission(submissionId, feedback) {
  const res = await api.post(`/api/assignment/submissions/${submissionId}/return`, { feedback });
  return res.data;
}

export async function requestResubmission(submissionId, feedback) {
  const res = await api.post(`/api/assignment/submissions/${submissionId}/resubmit`, { feedback });
  return res.data;
}

// Analytics

export async function getAssignmentAnalytics(assignmentId) {
  const res = await api.get(`/api/assignment/assignments/${assignmentId}/analytics`);
  return res.data;
}

// ---- Visa Application System ----

// Get visa requirements for a country
export async function getVisaRequirements(country) {
  const res = await api.get(`/api/visa/requirements?country=${country}`);
  return res.data;
}

// Create visa application
export async function createVisaApplication(data) {
  const res = await api.post('/api/visa/applications', data);
  return res.data;
}

// Get all user's visa applications
export async function getUserVisaApplications() {
  const res = await api.get('/api/visa/applications');
  return res.data;
}

// Get specific visa application
export async function getVisaApplication(applicationId) {
  const res = await api.get(`/api/visa/applications/${applicationId}`);
  return res.data;
}

// Update visa application (draft only)
export async function updateVisaApplication(applicationId, data) {
  const res = await api.patch(`/api/visa/applications/${applicationId}`, data);
  return res.data;
}

// Submit visa application
export async function submitVisaApplication(applicationId) {
  const res = await api.post(`/api/visa/applications/${applicationId}/submit`);
  return res.data;
}

// Delete visa application
export async function deleteVisaApplication(applicationId) {
  const res = await api.delete(`/api/visa/applications/${applicationId}`);
  return res.data;
}

// Upload visa document
export async function uploadVisaDocument(applicationId, documentType, file, expiryDate) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  if (expiryDate) {
    formData.append('expiryDate', expiryDate);
  }

  const res = await api.post(`/api/visa/applications/${applicationId}/documents`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
}

// Get application documents
export async function getVisaApplicationDocuments(applicationId) {
  const res = await api.get(`/api/visa/applications/${applicationId}/documents`);
  return res.data;
}
